import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfPoint, PdfBrush, PdfColor } from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/label';
import '../../pdf/draw/shapes';
import '../../pdf/draw/cut';

/**
 * Cesta (Road) - Path puzzle with road tiles
 *
 * Layout: 10 portrait pages.
 * Each page: a path shown on a 5x5 grid (top section) + 12 road tiles
 * to cut out (bottom section). Players must arrange tiles to recreate the path.
 *
 * Migrated from C# EduFrame.Img10.Generators.Cesta
 */
export class Cesta implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const margin2 = PdfUnit.FromMillimeters(3);

    const tile = (PdfSize.A4.width - 2 * margin) / 6;
    const plus = tile + margin2;
    const originStack = new PdfPoint(margin, PdfSize.A4.height - margin - 2 * tile);
    const originY = (originStack.y - 5 * tile - 4 * margin2) / 2;
    const originX = (PdfSize.A4.width - 5 * tile - 4 * margin2) / 2;
    const cutPoint = originStack.y - margin;

    const r1 = new PdfRect(originX, originY, 2 * tile + margin2, 2 * tile + margin2);
    const r2 = new PdfRect(originX + 3 * plus, originY + 4 * plus, 2 * tile + margin2, tile);

    const darkGray = new PdfBrush(PdfColor.fromRgb(169, 169, 169));

    const colors = GUtil.randomBrushes(opts.data.random, [
      [
        '#fcf4e4',
        '#f6eede',
        '#efe8d8',
        '#e8e3d2',
        '#e2ddcc',
        '#dbd7c6',
        '#d5d2c0',
        '#ceccba',
        '#c8c7b4',
        '#c1c1ae',
      ],
      [
        '#ddd4c7',
        '#e0d7ca',
        '#e4dacd',
        '#e7dcd0',
        '#eadfd3',
        '#ede2d6',
        '#f1e4d9',
        '#f4e7dc',
        '#f7eadf',
        '#fbece2',
      ],
      [
        '#85a739',
        '#8faf4d',
        '#9ab75e',
        '#a4c06e',
        '#afc87e',
        '#bad08e',
        '#c5d89e',
        '#d0e0ad',
        '#dbe9bc',
        '#e6f1cc',
      ],
      [
        '#fbe3f6',
        '#f8dff7',
        '#f4dcf8',
        '#f0d8f9',
        '#edd4fa',
        '#e9d1fb',
        '#e5cdfc',
        '#e2cafd',
        '#dec6fe',
        '#dac2fe',
      ],
    ]);

    interface Coord { x: number; y: number }

    const paths: Coord[][] = [
      [
        { x: 3, y: 0 },
        { x: 4, y: 0 },
        { x: 4, y: 1 },
        { x: 4, y: 2 },
        { x: 3, y: 2 },
        { x: 2, y: 2 },
        { x: 1, y: 2 },
        { x: 0, y: 2 },
        { x: 0, y: 3 },
        { x: 0, y: 4 },
        { x: 1, y: 4 },
        { x: 2, y: 4 },
      ],
      [
        { x: 0, y: 3 },
        { x: 0, y: 4 },
        { x: 1, y: 4 },
        { x: 2, y: 4 },
        { x: 2, y: 3 },
        { x: 2, y: 2 },
        { x: 2, y: 1 },
        { x: 2, y: 0 },
        { x: 3, y: 0 },
        { x: 4, y: 0 },
        { x: 4, y: 1 },
        { x: 4, y: 2 },
      ],
    ];

    for (let pageIdx = 0; pageIdx < opts.list.length; pageIdx++) {
      const page = pdf.addPage();
      const path = paths[opts.data.random.next(0, paths.length)];

      // Compute road connections for each tile
      const roads = path.map((p) => [
        path.some((j) => j.x === p.x - 1 && j.y === p.y), // left
        path.some((j) => j.x === p.x + 1 && j.y === p.y), // right
        path.some((j) => j.x === p.x && j.y === p.y - 1), // top
        path.some((j) => j.x === p.x && j.y === p.y + 1), // bottom
      ]);

      // Shuffle road tiles
      const shuffled = roads
        .map((r, i) => ({ r, key: opts.data.random.next(0, 1000000) }))
        .sort((a, b) => a.key - b.key)
        .map(({ r }) => r);

      // Background
      page.drawRectangle(
        null,
        colors[pageIdx % colors.length],
        new PdfRect(0, 0, PdfSize.A4.width, cutPoint)
      );

      // Cut line
      page.cutCross(new PdfPoint(0, cutPoint), true, false);

      // Labels
      page.drawLabel(opts.list[pageIdx].left, { rect: r1 });
      page.drawLabel(opts.list[pageIdx].right, { rect: r2 });

      // Path grid
      for (const item of path) {
        const r = new PdfRect(originX + item.x * plus, originY + item.y * plus, tile, tile);
        page.drawRectangle(null, PdfBrush.White, r);
        page.drawRectangle(GUtil.basePen, null, r);
      }

      // Road tiles to cut out
      for (let y = 0; y < 2; y++) {
        for (let x = 0; x < 6; x++) {
          const index = y * 6 + x;
          if (index >= shuffled.length) continue;

          const r = new PdfRect(originStack.x + x * tile, originStack.y + y * tile, tile, tile);
          page.drawRectangle(null, colors[pageIdx % colors.length], r);
          page.drawRoad(
            r,
            shuffled[index][0],
            shuffled[index][1],
            shuffled[index][2],
            shuffled[index][3],
            darkGray
          );
          page.drawRectangle(GUtil.cutPen, null, r);
        }
      }
    }
  }
}
